import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Phone, ArrowLeft, MapPin, Navigation, Clock, Tag } from 'lucide-react'
import { motion } from 'framer-motion'
import { Footer } from '@/components/layout'

export interface StoreFeature {
  type: 'Feature'
  geometry: { type: 'Point'; coordinates: [number, number] }
  properties: {
    tipologia: string
    tipologia_slug: string
    name: string
    phone: string
    address: string
    link: string
    icon: string
    linee: string[]
    orari?: { giorno: string; fascia: string }[]
    promo?: string[]
  }
}

export const typeColorMap: Record<string, string> = {
  'nashi-argan-store': '#B08D62',
  'nashi-salon': '#CFA980',
  'salone-partner': '#8B7B73',
  'by-nashi': '#5C4F4B',
  'salone-total-nashi': '#3B312E',
  'salone-rivenditore': '#6B5F5A',
}

function createCustomIcon(tipologiaSlug: string) {
  const color = typeColorMap[tipologiaSlug] || '#B08D62'
  return L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="background-color:${color};width:28px;height:28px;border-radius:50%;border:2px solid #FFFAF3;box-shadow:0 2px 6px rgba(59,49,46,0.35);display:flex;align-items:center;justify-content:center;">
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFFAF3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
    </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  })
}

export function StoreDetailPage() {
  const { slug, lang } = useParams<{ slug: string; lang: string }>()
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const [store, setStore] = useState<StoreFeature | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('/data/stores.json')
      .then((r) => r.json())
      .then((data: { features: StoreFeature[] }) => {
        const found = data.features.find((f) => f.properties.link === slug)
        setStore(found || null)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [slug])

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-cream">
        <p className="text-bruno-muted text-sm tracking-wide uppercase">{t('aria.loading', 'Caricamento')}</p>
      </main>
    )
  }

  if (!store) {
    return (
      <main className="min-h-screen bg-cream pt-24">
        <div className="nashi-container text-center py-24">
          <h1 className="font-display text-bruno text-2xl mb-4">
            {t('storeDetail.notFound', 'Store non trovato')}
          </h1>
          <button
            onClick={() => navigate(-1)}
            className="text-sm uppercase tracking-[0.16em] text-argan-deep underline hover:text-bruno transition-colors"
          >
            {t('storeDetail.goBack', 'Torna indietro')}
          </button>
        </div>
        <Footer />
      </main>
    )
  }

  const color = typeColorMap[store.properties.tipologia_slug] || '#B08D62'
  const coords: [number, number] = [store.geometry.coordinates[1], store.geometry.coordinates[0]]

  return (
    <main className="min-h-screen bg-cream pt-24">
      <div className="nashi-container pb-12">
        <motion.button
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(`/${lang}/store-locator`)}
          className="flex items-center gap-2 text-sm text-bruno-muted hover:text-argan-deep transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          {t('storeDetail.backToStoreLocator', 'Torna allo Store Locator')}
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12"
        >
          {/* Details */}
          <div className="space-y-6">
            <div>
              <span
                className="inline-block text-[10px] uppercase tracking-[0.16em] text-white font-medium px-3 py-1 mb-3"
                style={{ backgroundColor: color }}
              >
                {store.properties.tipologia}
              </span>
              <h1
                className="font-display text-bruno"
                style={{ fontSize: 'clamp(1.75rem, 3vw, 2.75rem)', lineHeight: 1.1, letterSpacing: '-0.015em' }}
              >
                {store.properties.name}
              </h1>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-argan-deep mt-0.5 shrink-0" strokeWidth={1.5} />
                <p className="text-bruno-soft leading-relaxed">{store.properties.address}</p>
              </div>

              {store.properties.phone && (
                <a
                  href={`tel:${store.properties.phone}`}
                  className="flex items-center gap-3 text-bruno hover:text-argan-deep transition-colors"
                >
                  <Phone className="w-5 h-5 text-argan-deep shrink-0" strokeWidth={1.5} />
                  <span>{store.properties.phone}</span>
                </a>
              )}

              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(store.properties.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] py-2.5 px-4 border border-bruno text-bruno hover:bg-bruno hover:text-cream transition-colors"
              >
                <Navigation className="w-4 h-4" strokeWidth={1.5} />
                {t('storeDetail.getDirections', 'Indicazioni stradali')}
              </a>
            </div>

            {store.properties.linee.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.24em] text-argan-deep font-medium mb-3">
                  {t('storeDetail.availableLines', 'Linee disponibili')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {store.properties.linee.map((line) => (
                    <span
                      key={line}
                      className="text-[11px] uppercase tracking-[0.12em] px-3 py-1.5 border border-bruno/20 text-bruno-soft bg-white/60"
                    >
                      {line.replace(/-/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {store.properties.orari && store.properties.orari.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-argan-deep" strokeWidth={1.5} />
                  <p className="text-[10px] uppercase tracking-[0.24em] text-argan-deep font-medium">
                    {t('storeDetail.openingHours', 'Orari di apertura')}
                  </p>
                </div>
                <div className="space-y-2">
                  {store.properties.orari.map((o) => (
                    <div
                      key={o.giorno}
                      className="flex justify-between text-sm border-b border-bruno/10 pb-1.5 last:border-0"
                    >
                      <span className="text-bruno">{o.giorno}</span>
                      <span className="text-bruno-soft">{o.fascia}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {store.properties.promo && store.properties.promo.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="w-4 h-4 text-argan-deep" strokeWidth={1.5} />
                  <p className="text-[10px] uppercase tracking-[0.24em] text-argan-deep font-medium">
                    {t('storeDetail.activePromotions', 'Promozioni attive')}
                  </p>
                </div>
                <div className="space-y-2">
                  {store.properties.promo.map((p, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 text-sm text-bruno-soft bg-white/60 border border-bruno/10 p-3"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-argan-deep shrink-0 mt-1.5" />
                      <span>{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Map */}
          <div className="h-[400px] lg:h-auto min-h-[400px] rounded-sm overflow-hidden border border-bruno/10 bg-white">
            <MapContainer center={coords} zoom={15} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={coords} icon={createCustomIcon(store.properties.tipologia_slug)}>
                <Popup>
                  <div className="p-1 min-w-[180px]">
                    <p className="font-display text-bruno text-base">{store.properties.name}</p>
                    <p className="text-sm text-bruno-soft mt-1 leading-relaxed">{store.properties.address}</p>
                  </div>
                </Popup>
              </Marker>
            </MapContainer>
          </div>
        </motion.div>
      </div>
      <Footer />
    </main>
  )
}
